import {
  useFundBasket,
  useFundBasketClients,
  useFundBasketLinkedSips,
  useNotifyFundBasketRebalance,
  useRebalanceFundBasketLinkedSips,
} from "creso-core/hooks";
import {
  TransactionOrderType,
  TRANSACTION_CLIENT_STATES,
} from "creso-core/constants";
import {
  convertToCurrency,
  formatAsCrore,
  getInitials,
} from "creso-core/utils";
import { format } from "date-fns";
import { forwardRef, useMemo, useState } from "react";
import {
  ChevronRightIcon,
  InfoIcon,
  Search,
  ShoppingCartIcon,
  UserIcon,
  XIcon,
} from "lucide-react";
import {
  ColumnDef,
  getCoreRowModel,
  RowSelectionState,
  useReactTable,
} from "@tanstack/react-table";
import {
  Badge,
  Button,
  Checkbox,
  cn,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  DataTable,
  SearchInput,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "web-design-system";
import { Avatar } from "web-core";
import { Link, useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import {
  ClientSearch,
  ClientSearchTriggerProps,
} from "@core/components/client-search";
import { LinkedSipEmptyStateIcon } from "@funds/components/baskets/icons/linked-sip-empty-state";
import {
  FundBasketLinkedSip as FundBasketLinkedSipRow,
  FundBasketLinkedSipMatchState,
} from "creso-core/types";
import { handleError } from "@/utils/error";
import { FundBasketLinkedSipChangesDialog } from "./fund-basket-linked-sip-changes-dialog";

const BASKET_MAPPING_BADGE: Record<
  FundBasketLinkedSipMatchState,
  { label: string; className: string }
> = {
  OUTDATED: {
    label: "Previous Version",
    className: "bg-tag-blue text-tag-blue-foreground",
  },
  PARTIAL: {
    label: "Custom Version",
    className: "bg-tag-slate text-tag-slate-foreground",
  },
  MATCHED: {
    label: "Current Version",
    className: "bg-tag-green text-tag-green-foreground",
  },
};

const SearchClientTrigger = forwardRef<
  HTMLDivElement,
  ClientSearchTriggerProps
>(({ onClick, open, client, display, onClear }, ref) => (
  <div ref={ref} className="relative w-full">
    <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
    <button
      type="button"
      onClick={onClick}
      aria-expanded={open}
      className="h-9 w-full truncate rounded-md border border-input bg-background pl-9 pr-8 text-left text-sm"
    >
      <span className={cn(!client && "text-muted-foreground")}>{display}</span>
    </button>
    {client && (
      <XIcon
        className="absolute right-3 top-1/2 size-4 -translate-y-1/2 cursor-pointer text-muted-foreground"
        onClick={onClear}
      />
    )}
  </div>
));
SearchClientTrigger.displayName = "SearchClientTrigger";

function LinkedSipEmptyState({ basketId }: { basketId: number }) {
  const navigate = useNavigate();
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);

  return (
    <div className="flex flex-col flex-1 items-center justify-center gap-6 text-center">
      <LinkedSipEmptyStateIcon />
      <span className="text-sm text-muted-foreground">
        No SIPs have been created for this basket
      </span>
      <div className="flex w-full max-w-md items-center gap-3">
        <ClientSearch
          status={TRANSACTION_CLIENT_STATES}
          trigger={SearchClientTrigger}
          onSelect={(v) => setSelectedClientId(v?.id ?? null)}
        />
        <Button
          disabled={!selectedClientId}
          className="shrink-0"
          onClick={() =>
            navigate({
              to: ".",
              search: (prev) => ({
                ...prev,
                modal: "transaction",
                collection_id: basketId,
                collection_type: "basket",
                type: TransactionOrderType.XSIP,
                client_id: selectedClientId as number,
              }),
            })
          }
        >
          Start SIP
        </Button>
      </div>
    </div>
  );
}

function EmptySearchResults() {
  return (
    <div className="text-muted-foreground py-12 text-sm w-full flex items-center justify-center">
      No clients found
    </div>
  );
}

export function FundBasketLinkedSip({ basketId }: { basketId: number }) {
  const { data: basket } = useFundBasket(basketId);
  const { data, isLoading } = useFundBasketLinkedSips(basketId);
  const [search, setSearch] = useState("");
  const [changesClientId, setChangesClientId] = useState<number | null>(null);
  const [changesBatchId, setChangesBatchId] = useState<number | null>(null);
  const [selecting, setSelecting] = useState(false);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const navigate = useNavigate();

  const { mutateAsync: rebalance, isPending: isUpdatingSelected } =
    useRebalanceFundBasketLinkedSips(basketId);

  const pendingUpdateSips = useMemo(
    () =>
      (data ?? []).filter(
        (sip) =>
          (sip.match_state === "OUTDATED" || sip.match_state === "PARTIAL") &&
          sip.changes_in_cart !== "all"
      ),
    [data]
  );

  const allSipsMatch = useMemo(
    () => !!data?.length && data.every((sip) => sip.match_state === "MATCHED"),
    [data]
  );

  const filteredData = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return data ?? [];
    return (data ?? []).filter((sip) =>
      sip.client_name?.toLowerCase().includes(query)
    );
  }, [data, search]);

  const handleStartSelecting = () => {
    const defaultSelection: RowSelectionState = {};
    pendingUpdateSips.forEach((sip) => {
      defaultSelection[String(sip.batch_id)] = true;
    });
    setRowSelection(defaultSelection);
    setSelecting(true);
  };

  const handleCancelSelecting = () => {
    setSelecting(false);
    setRowSelection({});
  };

  const handleUpdateSelected = async () => {
    const batchIds = Array.from(
      new Set(
        (data ?? [])
          .filter((sip) => rowSelection[String(sip.batch_id)])
          .map((sip) => sip.batch_id)
      )
    );
    if (!batchIds.length) return;
    try {
      const result = await rebalance({ batch_ids: batchIds, dry_run: false });
      const updatedText = `Updated ${result.changes_needed_count} SIP${result.changes_needed_count === 1 ? "" : "s"}`;
      const blockedText = result.blocked_count
        ? `, ${result.blocked_count} need manual review`
        : "";
      toast.success(`${updatedText}${blockedText}`);
      handleCancelSelecting();
    } catch (error) {
      handleError(error, "Failed to update SIPs");
    }
  };

  const columns = useMemo<ColumnDef<FundBasketLinkedSipRow>[]>(
    () => [
      {
        id: "select",
        header: ({ table }) => (
          <Checkbox
            className="mt-1"
            checked={
              table.getIsAllRowsSelected()
                ? true
                : table.getIsSomeRowsSelected()
                  ? "indeterminate"
                  : false
            }
            onCheckedChange={(value) => {
              if (value === "indeterminate") return;
              table.toggleAllRowsSelected(!!value);
            }}
            aria-label="Select all"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            className="mt-1"
            checked={row.getIsSelected()}
            disabled={!row.getCanSelect()}
            onCheckedChange={(value) => {
              if (value === "indeterminate") return;
              row.toggleSelected(!!value);
            }}
            onClick={(event) => event.stopPropagation()}
            aria-label={`Select ${row.original.client_name}`}
          />
        ),
        size: 30,
      },
      {
        id: "client_name",
        header: "Client name",
        size: 320,
        cell: ({ row }) => (
          <div className="flex flex-row gap-3 items-center">
            <Avatar
              fallback={getInitials(row.original.client_name ?? "")}
              url=""
              size="sm"
            />
            <Link
              to="/clients/$id/portfolio"
              params={{ id: row.original.client_id.toString() }}
              search={{ detail: "sip" }}
            >
              {row.original.client_name}
            </Link>
          </div>
        ),
      },
      {
        id: "start_date",
        header: () => <div className="text-right w-full">Start date</div>,
        cell: ({ row }) => (
          <div className="text-right w-full">
            {row.original.start_date
              ? format(new Date(row.original.start_date), "dd MMM yyyy")
              : "-"}
          </div>
        ),
        minSize: Number.MAX_SAFE_INTEGER,
      },
      {
        id: "sip_amount",
        header: () => <div className="text-right">SIP amount</div>,
        cell: ({ row }) => (
          <div className="text-right">
            {convertToCurrency(row.original.sip_amount)}
          </div>
        ),
        minSize: Number.MAX_SAFE_INTEGER,
      },
      {
        id: "basket_mapping",
        header: () => <div className="text-center">Basket mapping</div>,
        cell: ({ row }) => {
          const badge = BASKET_MAPPING_BADGE[row.original.match_state] ?? {
            label: row.original.match_state,
            className: "bg-tag-slate text-tag-slate-foreground",
          };
          return (
            <div className="flex gap-4 justify-center">
              <Badge
                variant="custom"
                className={cn("rounded-sm", badge.className)}
              >
                {badge.label}
              </Badge>
            </div>
          );
        },
        minSize: Number.MAX_SAFE_INTEGER,
      },
      {
        id: "actions",
        header: "",
        meta: { className: "text-right" },
        cell: ({ row }) => (
          <div className="flex items-center justify-end gap-3">
            {row.original.changes_in_cart === "all" && (
              <Button
                onClick={(event) => {
                  event.stopPropagation();
                  navigate({
                    to: ".",
                    search: {
                      modal: "transaction",
                      action: "view",
                      client_id: row.original.client_id,
                    },
                  });
                }}
                size="icon"
                variant="ghost"
                className="relative"
              >
                <ShoppingCartIcon className="size-4" />
                <span className="absolute top-2 right-2 size-1.5 rounded-full bg-destructive" />
              </Button>
            )}
            {row.original.match_state !== "MATCHED" && (
              <Button
                variant="outline"
                size="sm"
                onClick={(event) => {
                  event.stopPropagation();
                  setChangesClientId(row.original.client_id);
                  setChangesBatchId(row.original.batch_id);
                }}
              >
                View changes
              </Button>
            )}
          </div>
        ),
        minSize: Number.MAX_SAFE_INTEGER,
      },
    ],
    [navigate]
  );

  const columnVisibility = useMemo(
    () => ({
      select: selecting,
      actions: basket?.basket_type !== "curated" && !allSipsMatch,
    }),
    [selecting, basket, allSipsMatch]
  );

  const showNotifyAll =
    !search && data?.some((f) => f.changes_in_cart === "all");

  const table = useReactTable({
    data: filteredData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => String(row.batch_id),
    state: { rowSelection, columnVisibility },
    onRowSelectionChange: setRowSelection,
    enableRowSelection: (row) =>
      selecting && row.original.match_state !== "MATCHED",
  });

  const notifyFundBasketRebalanceMutation = useNotifyFundBasketRebalance(
    basket?.id as number
  );

  if (isLoading) return null;

  if (!data?.length) return <LinkedSipEmptyState basketId={basketId} />;

  const selectedCount = Object.keys(rowSelection).length;

  return (
    <div className="flex flex-col gap-4">
      {selecting ? (
        <div className="flex items-center gap-4 bg-slate-200 rounded-lg px-4 py-3">
          <span className="text-sm font-semibold">
            Update plans to the latest basket profile
          </span>
          <div className="ml-auto flex items-center gap-3">
            <span className="text-sm text-muted-foreground">
              {selectedCount} selected
            </span>
            <Button
              disabled={!selectedCount || isUpdatingSelected}
              onClick={handleUpdateSelected}
            >
              Update to Basket
            </Button>
            <Button
              variant="ghost"
              size="icon"
              disabled={isUpdatingSelected}
              onClick={handleCancelSelecting}
              aria-label="Cancel selection"
            >
              <XIcon className="size-4" />
            </Button>
          </div>
        </div>
      ) : (
        pendingUpdateSips.length > 0 && (
          <div className="flex items-center gap-2 rounded-lg border border-border bg-tag-slate px-4 py-3">
            <InfoIcon className="size-4 shrink-0" />
            <span className="text-sm">
              {pendingUpdateSips.length} client
              {pendingUpdateSips.length === 1 ? "" : "s"} have SIPs using a
              different version of the basket
            </span>
            <Button
              variant="outline"
              className="ml-auto bg-background"
              onClick={handleStartSelecting}
            >
              Update SIPs
            </Button>
          </div>
        )
      )}
      <div className="flex items-center justify-between">
        <SearchInput
          placeholder="Search for clients"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className="max-w-sm bg-background"
        />
        {showNotifyAll && (
          <Button
            className="shrink-0"
            disabled={notifyFundBasketRebalanceMutation.isPending}
            onClick={() => {
              notifyFundBasketRebalanceMutation
                .mutateAsync()
                .then(() =>
                  toast.success("Rebalance notification sent successfully")
                )
                .catch((e) => handleError(e, "Failed to send notification"));
            }}
          >
            Notify all
          </Button>
        )}
      </div>
      <DataTable
        table={table}
        pagination={false}
        emptyState={EmptySearchResults}
      />
      <FundBasketLinkedSipChangesDialog
        basketId={basketId}
        clientId={changesClientId}
        batchId={changesBatchId}
        onOpenChange={(open) => {
          if (!open) {
            setChangesClientId(null);
            setChangesBatchId(null);
          }
        }}
      />
    </div>
  );
}

export function FundBasketClients({ basketId }: { basketId: number }) {
  const { data } = useFundBasketClients(basketId);

  if (!data?.client_count) return null;

  return (
    <Collapsible className="w-full bg-linear-to-b rounded-lg from-tag-yellow/50 to-background border group border-border">
      <CollapsibleTrigger className="flex w-full py-5 px-4 flex-row text-sm gap-2 items-center">
        <UserIcon className="size-4" />
        <span className="font-semibold text-sm">
          {data.client_count} ongoing SIPs linked to this basket
        </span>
        <span className="ml-auto text-sm text-muted-foreground">
          Total Amount:
        </span>
        <span className="text-sm font-semibold ml-1">
          {formatAsCrore(data.total_sip_amount)}
        </span>
        <ChevronRightIcon className="size-5 ml-1 group-data-[state=open]:rotate-90" />
      </CollapsibleTrigger>
      <CollapsibleContent className="max-h-80 px-4 overflow-y-auto">
        <div className="px-9 pb-4">
          <Table className="w-full text-sm">
            <TableHeader>
              <TableRow className="text-left border-none">
                <TableHead className="py-3 font-semibold">
                  Client Name
                </TableHead>
                <TableHead className="py-3 font-semibold">Added On</TableHead>
                <TableHead className="py-3 font-semibold text-right">
                  SIP Amount
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.clients?.map((client) => (
                <TableRow
                  key={client.client_id}
                  className="border-none hover:bg-transparent"
                >
                  <TableCell className="py-3">
                    <div className="flex flex-row gap-3 items-center">
                      <Avatar
                        fallback={getInitials(client.client_name ?? "")}
                        url=""
                        size="sm"
                      />
                      <Link
                        to="/clients/$id/portfolio"
                        params={{ id: client.client_id.toString() }}
                        search={{ detail: "sip" }}
                      >
                        {client.client_name}
                      </Link>
                    </div>
                  </TableCell>
                  <TableCell className="py-3">
                    {client.first_used_at
                      ? format(new Date(client.first_used_at), "dd MMM yyyy")
                      : "-"}
                  </TableCell>
                  <TableCell className="py-3 text-right">
                    {convertToCurrency(client.active_sip_amount)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
